import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';

interface BrandRow {
  id: string;
  brand_name: string;
}

interface FavoriteRow {
  brand_id: string | null;
  brand_name: string | null;
}

export interface BrandFilterOption {
  value: string;
  label: string;
  iconAfter?: string;
}

/**
 * 商品一覧のブランドフィルタ用プルダウン選択肢を生成する。
 * - source ('manual' | 'amazon') で該当ソースの商品が存在するブランドのみ取得
 * - お気に入りブランド (user_favorite_brands) を上位に固め、金色の星アイコンを付与
 *   (お気に入りのトグル自体はブランド管理画面で行う想定。ここでは表示のみ)
 */
export function useBrandFilterOptions(source: 'manual' | 'amazon'): BrandFilterOption[] {
  const { data: brandsData } = useQuery({
    queryKey: ['brands', source],
    queryFn: async () => {
      const response = await apiClient.get('/brands', { params: { source } });
      return response.data as { brands: BrandRow[] };
    },
    staleTime: 5 * 60 * 1000,
  });

  // お気に入りブランド (ブランド管理画面と共有)
  const { data: favoritesData } = useQuery({
    queryKey: ['user', 'favorite-brands'],
    queryFn: async () => {
      const response = await apiClient.get('/user/favorite-brands');
      return response.data as { favorites: FavoriteRow[] };
    },
    staleTime: 60_000,
  });

  return useMemo(() => {
    const brands = brandsData?.brands ?? [];
    const favoriteIds = new Set<string>();
    for (const fav of favoritesData?.favorites ?? []) {
      if (fav.brand_id) favoriteIds.add(fav.brand_id);
    }

    // お気に入りを上位に固める (同グループ内は元の順序を維持＝stable sort)
    const sorted = brands
      .map((brand, index) => ({ brand, isFavorite: favoriteIds.has(brand.id), index }))
      .sort((a, b) => {
        if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
        return a.index - b.index;
      });

    return [
      { value: '', label: 'すべて' },
      ...sorted.map(({ brand, isFavorite }) => ({
        value: brand.id,
        label: brand.brand_name,
        ...(isFavorite ? { iconAfter: '/star_1_gold.svg' } : {}),
      })),
    ];
  }, [brandsData, favoritesData]);
}
